/**
 * Client HTTP de l'API GestClinic.
 *
 * - Préfixe /api/v1, JSON par défaut, cookies envoyés (jeton de rafraîchissement httpOnly).
 * - Jeton d'accès gardé en mémoire uniquement (jamais dans localStorage) : D-15.
 * - Sur 401, tente UNE fois de renouveler le jeton (un seul appel partagé), puis rejoue.
 * - Erreurs RFC 7807 converties en `ApiError` avec un `code` métier stable.
 */

export const API_BASE = '/api/v1';

export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  errors?: Record<string, unknown>;
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}

let accessToken: string | null = null;
let sessionLostHandler: ((code: string) => void) | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Appelé quand la session est définitivement perdue (expirée, révoquée). */
export function onSessionLost(handler: ((code: string) => void) | null): void {
  sessionLostHandler = handler;
}

/** Renouvelle le jeton d'accès via le cookie. Un seul appel réseau même si plusieurs requêtes échouent. */
export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          accessToken = null;
          return false;
        }
        const data = (await response.json()) as { access: string };
        accessToken = data.access;
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Ne pas tenter de rafraîchir la session sur 401 (connexion, rafraîchissement). */
  noRetry?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('/health') || path.startsWith(API_BASE) ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('json')) {
    try {
      const data = (await response.json()) as Partial<ProblemDetails>;
      return new ApiError({
        title: data.title ?? response.statusText,
        status: data.status ?? response.status,
        code: data.code ?? 'ERROR',
        ...data,
      } as ProblemDetails);
    } catch {
      /* corps illisible : erreur générique ci-dessous */
    }
  }
  return new ApiError({
    title: response.statusText || 'Erreur réseau',
    status: response.status,
    code: response.status >= 500 ? 'SERVER_ERROR' : 'ERROR',
  });
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, query, signal, headers = {} } = options;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const finalHeaders: Record<string, string> = { Accept: 'application/json', ...headers };
  if (body !== undefined && !isForm) finalHeaders['Content-Type'] = 'application/json';
  if (accessToken) finalHeaders.Authorization = `Bearer ${accessToken}`;
  return fetch(buildUrl(path, query), {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    credentials: 'include',
    signal,
  });
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && !options.noRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send(path, options);
    }
    if (response.status === 401) {
      const error = await toApiError(response);
      sessionLostHandler?.(error.code);
      throw error;
    }
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Libellé d'erreur présentable à l'utilisateur. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'SERVER_ERROR') return 'Le serveur a rencontré une erreur. Réessayez.';
    return error.problem.detail ?? error.message;
  }
  if (error instanceof TypeError) return 'Serveur injoignable. Vérifiez la connexion.';
  return 'Une erreur inattendue est survenue.';
}

/** Fichier renvoyé par l'API (PDF à afficher…), avec la même authentification. */
export async function apiBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const request = { ...options, headers: { ...options.headers, Accept: '*/*' } };
  let response = await send(path, request);
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await send(path, request);
  }
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

/** Télécharge un fichier renvoyé par l'API (export CSV…) et le propose à l'enregistrement. */
export async function apiDownload(
  path: string,
  options: RequestOptions & { filename: string },
): Promise<void> {
  const { filename, ...rest } = options;
  const request = { ...rest, headers: { ...rest.headers, Accept: '*/*' } };
  let response = await send(path, request);
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await send(path, request);
  }
  if (!response.ok) throw await toApiError(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
